import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from '../redux/store';
import { getOrders, getOperationalSummary, getOrdersStatistics } from '../redux/slices/order';
import { getTabs } from '../redux/slices/tab';
import { getInventoryStatement, getInventorySummary } from '../redux/slices/inventory';
import useAuth from './useAuth';
import { VenueManager } from '../@types/venue';

/**
 * Hook that watches tenantVenue changes and dispatches necessary actions
 * to refetch data when the tenant venue changes.
 * 
 * This ensures that when a user switches between venues or organization,
 * all relevant data (orders, tabs, inventory) is automatically refreshed.
 */
export default function useTenantVenueEffect() {
  const dispatch = useDispatch();
  const { user, isInitialized, isAuthenticated } = useAuth();
  const { tenantVenue } = useSelector((state) => state.venue);
  const previousTenantVenueRef = useRef<string | null | VenueManager>(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !user) {
      return;
    }

    // Wait for tenantVenue to be initialized (either from persistence or from user.organization_id)
    if (!tenantVenue || tenantVenue === 'all') {
      if (user.organization_id) {
        // This will be handled by VenuePopover's useEffect
        // We just need to wait for it to be set
        return;
      }
      // If no organization_id, skip
      return;
    }

    // Check if tenantVenue has changed
    const hasChanged = 
      previousTenantVenueRef.current === null ||
      JSON.stringify(previousTenantVenueRef.current) !== JSON.stringify(tenantVenue);

    // On first load (after persistence is restored), refetch data if tenantVenue is set
    if (!isInitializedRef.current) {
      previousTenantVenueRef.current = tenantVenue;
      isInitializedRef.current = true;
      
      // Refetch data on initial load if tenantVenue is restored from persistence
      console.log('Initial load with tenantVenue, refetching data...', tenantVenue);
      dispatch(getOrders());
      dispatch(getTabs());
      dispatch(getInventoryStatement());
      dispatch(getInventorySummary());
      return;
    }

    // If tenantVenue has changed after initialization, refetch all data
    if (hasChanged) {
      console.log('Tenant venue changed, refetching data...', {
        previous: previousTenantVenueRef.current,
        current: tenantVenue,
      });

      const venueId = typeof tenantVenue === 'object' && tenantVenue !== null
        ? (tenantVenue as VenueManager).id
        : undefined;

      dispatch(getOrders());
      dispatch(getTabs());
      dispatch(getInventoryStatement());
      dispatch(getInventorySummary());
      dispatch(getOperationalSummary());
      dispatch(getOrdersStatistics(venueId));

      previousTenantVenueRef.current = tenantVenue;
    }
  }, [tenantVenue, user, isInitialized, isAuthenticated, dispatch]);
}

